import { useEffect, useRef } from 'react';
import type { Participant } from 'livekit-client';

import './SpeakingMeter.css';

/**
 * Микрофон, который наполняется от голоса участника.
 *
 * `audioLevel` в LiveKit обновляется десятки раз в секунду. Гонять его
 * через состояние React нельзя — на встрече из десяти человек это десять
 * перерисовок в кадре. Поэтому значение читается прямо в кадре анимации
 * и пишется в CSS-переменную: браузер отрисует это без участия React.
 *
 * Один цикл анимации на плитку, и тот останавливается, когда микрофон
 * выключен.
 */
export function SpeakingMeter({
  participant,
  muted,
  size = 26,
}: {
  participant: Participant;
  muted: boolean;
  size?: number;
}) {
  const hostRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || muted) {
      host?.style.setProperty('--level', '0');
      return;
    }

    let frame = 0;
    let smoothed = 0;

    const tick = () => {
      // Речь редко превышает 0.3 — растягиваем рабочий диапазон.
      const level = Math.min(1, (participant.audioLevel ?? 0) * 3.2);
      // Быстрый подъём, медленный спад: заполнение не дёргается.
      smoothed = level > smoothed ? level : smoothed * 0.82 + level * 0.18;

      host.style.setProperty('--level', smoothed.toFixed(3));
      host.style.setProperty('--lit', smoothed > 0.06 ? '1' : '0');
      frame = requestAnimationFrame(tick);
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [participant, muted]);

  return (
    <span
      ref={hostRef}
      className={`vu ${muted ? 'vu--muted' : ''}`}
      style={{ width: size, height: size }}
      aria-hidden
    >
      <svg viewBox="0 0 24 24" width={size} height={size}>
        {/* Контур микрофона — виден всегда. */}
        <path className="vu__shell" d={MIC_BODY} />
        <path className="vu__shell" d={MIC_STAND} />

        {/* Заливка капсулы. Обрезается сверху вниз через CSS clip-path:
            в отличие от SVG-атрибутов, он понимает calc() с переменными. */}
        <path className="vu__fill" d={MIC_BODY} />

        {muted && <path className="vu__slash" d="M4 4l16 16" />}
      </svg>
    </span>
  );
}

const MIC_BODY = 'M12 3.6a2.9 2.9 0 0 1 2.9 2.9v5a2.9 2.9 0 0 1-5.8 0v-5A2.9 2.9 0 0 1 12 3.6Z';
const MIC_STAND = 'M6 11a6 6 0 0 0 12 0M12 17v3.2M9 20.4h6';
